import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, Heart, Sparkles, Zap, Target, Star, Users, BookOpen, Video, Calendar } from 'lucide-react';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const { grandCelebrate } = useCelebration();
  const [step, setStep] = useState(0); // Start at 0 for welcome message
  const [formData, setFormData] = useState({
    community_agreement_accepted: false,
    focus_area: '',
    notification_preferences: {
      messages: true,
      community: true,
      events: true,
      marketing: false
    }
  });
  const [loading, setLoading] = useState(false);

  const totalSteps = 6; // Now 6 steps with dedicated welcome step

  const focusAreas = [
    { id: 'boundaries', label: 'Boundaries & Power', icon: Zap, description: 'Honor your no. Reclaim your peace.' },
    { id: 'influence', label: 'Influence & Presence', icon: Sparkles, description: 'Amplify your voice without dimming your light' },
    { id: 'energy', label: 'Energy & Wellness', icon: Heart, description: 'Rest is your birthright, not a reward' },
    { id: 'purpose', label: 'Purpose & Vision', icon: Target, description: 'Clarify your calling and lead from your soul' }
  ];

  const handleNext = async () => {
    if (step === 2 && !formData.community_agreement_accepted) {
      toast.error('Please accept the community agreement to continue');
      return;
    }
    if (step === 4 && !formData.focus_area) {
      toast.error('Please select a focus area');
      return;
    }

    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      // Complete onboarding
      setLoading(true);
      try {
        await updateProfile({
          ...formData,
          onboarding_completed: true
        });
        grandCelebrate('confetti', 'milestone');
        toast.success('Welcome home, Sis! Your sanctuary is ready.');
        setTimeout(() => navigate('/dashboard'), 2000);
      } catch (error) {
        toast.error('Failed to complete onboarding');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Step {step + 1} of {totalSteps}</span>
            <span className="text-sm text-forest font-medium">
              {step === 0 && 'Welcome Home'}
              {step === 1 && 'You Belong Here'}
              {step === 2 && 'Community Agreement'}
              {step === 3 && 'Your Focus'}
              {step === 4 && 'Notifications'}
              {step === 5 && 'Get Started'}
            </span>
          </div>
          <div className="h-2 bg-sand rounded-full">
            <div 
              className="h-2 bg-terracotta rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        <Card className="card-base shadow-lg">
          {/* Step 0: Welcome Home - Detailed Welcome Message */}
          {step === 0 && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="relative w-full h-32 mb-4 rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=800&h=200&fit=crop"
                    alt="Sister Diamonds Community"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/60 to-transparent" />
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
                    <Sparkles className="h-8 w-8 text-white animate-pulse" />
                  </div>
                </div>
                <CardTitle className="font-heading text-3xl text-forest">
                  Welcome Home, {user?.full_name?.split(' ')[0] || 'Sis'}
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  You just made one of the most powerful decisions of your life.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gradient-to-br from-terracotta/5 to-forest/5 rounded-xl p-6 text-center">
                  <p className="text-lg text-forest leading-relaxed mb-4">
                    You are not here by accident. Something in your spirit said <span className="font-semibold italic">&ldquo;it&apos;s time&rdquo;</span> — time to be seen, held, and celebrated for the brilliant woman you already are.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    Sister Diamonds is not another place asking you to perform, prove, or produce. This is your sanctuary — a sisterhood of high-capacity Black women who understand the weight you carry and the glory you embody.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-heading text-lg text-forest text-center">What This Space Holds For You:</h3>
                  
                  {/* Add community image */}
                  <div className="relative h-40 rounded-xl overflow-hidden mb-4">
                    <img 
                      src="https://images.unsplash.com/photo-1573497701240-345a300b8d36?w=800&h=300&fit=crop"
                      alt="Sisters gathered together"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent" />
                  </div>
                  
                  <div className="grid gap-3">
                    <div className="flex items-start gap-3 p-3 bg-sand/30 rounded-lg">
                      <Heart className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-forest">Permission to exhale</p>
                        <p className="text-sm text-gray-500">No masks. No performance. Just you, fully held.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-sand/30 rounded-lg">
                      <Users className="h-5 w-5 text-forest flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-forest">Sisters who see you</p>
                        <p className="text-sm text-gray-500">Women who get it — because they live it too.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-sand/30 rounded-lg">
                      <Sparkles className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-forest">Tools for your becoming</p>
                        <p className="text-sm text-gray-500">Wisdom, coaching, and resources designed for your wholeness.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center pt-4 border-t border-sand">
                  <p className="text-terracotta font-medium text-lg">
                    You don&apos;t have to carry it all alone anymore.
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Let&apos;s get you settled into your sanctuary.
                  </p>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 1: You Belong Here */}
          {step === 1 && (
            <>
              <CardHeader className="text-center">
                <div className="w-20 h-20 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-10 w-10 text-terracotta" />
                </div>
                <CardTitle className="font-heading text-3xl text-forest">You Belong Here, Sis</CardTitle>
                <CardDescription className="text-base">
                  {user?.full_name?.split(' ')[0]}, you&apos;ve just stepped into a sanctuary of high-capacity, brilliant Black women who see you.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-center text-gray-600 leading-relaxed">
                  This is your space to breathe, become, and soar. Over the next 90 days, 
                  you&apos;ll have access to soul-nourishing resources designed specifically for women like us.
                </p>
                
                <div className="bg-sand/50 rounded-xl p-6">
                  <h3 className="font-heading text-lg text-forest mb-4 text-center">Here&apos;s What Awaits You:</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Sister Circles</p>
                        <p className="text-sm text-gray-500">Safe spaces to speak what&apos;s sacred</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="h-5 w-5 text-forest" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Soul Journaling</p>
                        <p className="text-sm text-gray-500">Pour out your heart, track your becoming</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Video className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">The Vault</p>
                        <p className="text-sm text-gray-500">Wisdom from Crystal & Dr. Nicole</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Sacred Gatherings</p>
                        <p className="text-sm text-gray-500">Live circles that transform</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-center text-terracotta font-medium">
                  You don&apos;t have to carry it all alone anymore.
                </p>
              </CardContent>
            </>
          )}

          {/* Step 2: Community Agreement */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest">Welcome, Sister Diamond</CardTitle>
                <CardDescription>
                  This is a sacred space. Our sisterhood thrives on trust, grace, and confidentiality. 
                  Please read and honor our community agreement.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-sand/50 rounded-xl p-6 space-y-4">
                  <h3 className="font-medium text-forest">As a Sister Diamond, I commit to:</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Holding space for each sister with respect and compassion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Honoring the sacredness of shared stories and experiences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Showing up authentically, without masks or performance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Offering and receiving truth with grace</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>Nurturing my own becoming while celebrating my sisters</span>
                    </li>
                  </ul>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="agreement" 
                    checked={formData.community_agreement_accepted}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, community_agreement_accepted: checked }))}
                  />
                  <Label htmlFor="agreement" className="text-forest">
                    I honor the Sister Diamonds community agreement
                  </Label>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Focus Area Selection (Personal Focus Path) */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest">Your Personal Focus Path</CardTitle>
                <CardDescription>
                  Where does your soul need attention right now? Choose your focus — you can always explore other areas too.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={formData.focus_area} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, focus_area: value }))}
                  className="space-y-4"
                >
                  {focusAreas.map((area) => (
                    <div key={area.id} className="flex items-center space-x-3">
                      <RadioGroupItem value={area.id} id={area.id} />
                      <Label 
                        htmlFor={area.id} 
                        className="flex items-center gap-3 cursor-pointer flex-1 p-4 rounded-xl hover:bg-sand/50 transition-colors"
                      >
                        <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                          <area.icon className="h-5 w-5 text-terracotta" />
                        </div>
                        <div>
                          <p className="font-medium text-forest">{area.label}</p>
                          <p className="text-sm text-gray-500">{area.description}</p>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </>
          )}

          {/* Step 4: Notification Preferences */}
          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest">Stay in the Circle</CardTitle>
                <CardDescription>
                  Choose how you would like us to reach you with soul food and sisterhood moments.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {[
                  { key: 'messages', label: 'Sister Messages', description: 'When a sister reaches out to connect' },
                  { key: 'community', label: 'Circle Updates', description: 'New conversations in your sacred spaces' },
                  { key: 'events', label: 'Gathering Reminders', description: 'Invitations to live circles and events' },
                  { key: 'marketing', label: 'Soul Food & Offerings', description: 'Wisdom drops and special invitations from Sister Diamonds' }
                ].map((pref) => (
                  <div key={pref.key} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-forest">{pref.label}</p>
                      <p className="text-sm text-gray-500">{pref.description}</p>
                    </div>
                    <Checkbox 
                      checked={formData.notification_preferences[pref.key]}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        notification_preferences: {
                          ...prev.notification_preferences,
                          [pref.key]: checked
                        }
                      }))}
                    />
                  </div>
                ))}
              </CardContent>
            </>
          )}

          {/* Step 5: Get Started */}
          {step === 5 && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-forest">Welcome Home, Sis</CardTitle>
                <CardDescription>
                  {user?.full_name?.split(' ')[0]}, your sanctuary is ready. Exhale. You made it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-forest text-white rounded-xl p-6 mb-6">
                  <h3 className="font-heading text-xl font-semibold mb-4">Your first steps into restoration:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">1</span>
                      <span>Receive this week&apos;s soul food transmission</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">2</span>
                      <span>Introduce yourself to your sisters in the Inner Circle</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">3</span>
                      <span>Explore the restoration library at your own pace</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    Your sacred focus: <span className="font-medium text-forest capitalize">{formData.focus_area?.replace('_', ' & ')}</span>
                  </p>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation */}
          <div className="p-6 pt-0 flex justify-between">
            {step > 0 ? (
              <Button variant="outline" onClick={handleBack} className="btn-outline">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            ) : (
              <div />
            )}
            <Button onClick={handleNext} className="btn-primary" disabled={loading}>
              {step === totalSteps - 1 ? (
                loading ? 'Opening the door...' : 'Step Into Your Sanctuary'
              ) : step === 0 ? (
                <>Let&apos;s Begin <ArrowRight className="ml-2 h-4 w-4" /></>
              ) : (
                <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
